import { z } from 'zod';

import {
  B2bSavingPlanCompartmentValues,
  ZodB2bSavingPlanCompartment,
} from '@Common/types/b2b/compartments';
import {
  postExploreMutualFundArbitrageSummaryEndpoint,
  refineArbitrageCompartmentEntries,
} from '@Common/types/explore/mutual_fund_arbitrage';
import { ZodProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodId } from '@Common/types/request';

// Normalizing a Taiga plan id is not idempotent: data parsed once already, as stored in a
// review request, keeps its plan ids as they are
const buildZodBoArbitrageFields = (
  zodSavingPlanId: typeof ZodProfileSavingPlanId | z.ZodString,
) => {
  const ZodAllocation = z.object({
    instrument_id: ZodId.nullable(),
    amount: z.number().int().positive(),
    // we receive numeric ids from the bo frontend, unlike in the app
    // it is then resolved server side to the employee's account
    saving_plan_id: ZodId.or(zodSavingPlanId),
  });
  const ZodEntry = z.object({
    compartment: ZodB2bSavingPlanCompartment.nullish(),
    sources: ZodAllocation.array().min(1),
    targets: ZodAllocation.array().min(1),
  });

  return z.object({
    b2b_employee_id: ZodId,
    notify_employee: z.boolean().default(true),
    arbitrages: ZodEntry.array().min(1).max(B2bSavingPlanCompartmentValues.length),
  });
};

const ZodBoArbitrageFields = buildZodBoArbitrageFields(ZodProfileSavingPlanId);

export type BoArbitrageAllocation = z.infer<
  typeof ZodBoArbitrageFields
>['arbitrages'][number]['sources'][number];

// The arbitrage an operator requests for an employee, created once a second operator approves it.
// The agreed terms are not part of it: the operator can't consent on behalf of the employee, the
// server applies the fixed ones.
export const ZodBoArbitrage = ZodBoArbitrageFields.superRefine((body, ctx) =>
  refineArbitrageCompartmentEntries(body.arbitrages, ctx),
);

export type BoArbitrage = z.infer<typeof ZodBoArbitrage>;

export const ZodStoredBoArbitrage = buildZodBoArbitrageFields(z.string());

export const ZodPostB2bIndividualOperationArbitrageBody = ZodBoArbitrageFields.extend({
  type: z.literal('arbitrage'),
}).superRefine((body, ctx) => refineArbitrageCompartmentEntries(body.arbitrages, ctx));

export type PostB2bIndividualOperationArbitrageBody = z.infer<
  typeof ZodPostB2bIndividualOperationArbitrageBody
>;
export type PostB2bIndividualOperationArbitrageRequest = z.input<
  typeof ZodPostB2bIndividualOperationArbitrageBody
>;

// The id is the employee id, only there because react-admin's create expects one in the response:
// it is not an operation id.
export const ZodPostB2bIndividualOperationArbitrageSummaryResponse =
  postExploreMutualFundArbitrageSummaryEndpoint.responseBody.extend({ id: ZodId });
export type PostB2bIndividualOperationArbitrageSummaryResponse = z.infer<
  typeof ZodPostB2bIndividualOperationArbitrageSummaryResponse
>;
